import {
  conversationMembersForDisplay,
  conversationUserNames,
} from '../utils/messaging/conversation';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import Component from '@glimmer/component';
import LinkButton from 'ember-simplepractice/components/spds/action/button/link';
import Tag from 'ember-simplepractice/components/spds/indicator/tag';
import styles from './conversation-header.module.scss';

export default class ConversationHeader extends Component {
  @service('spds/toast') toast;
  @service clientProfileNavigator;
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get conversation() {
    return this.args.conversation;
  }

  get isGroup() {
    return this.conversation.isGroup;
  }

  get memberCount() {
    return this.conversation.collapsedMembers.length;
  }

  get originalMemberCount() {
    return this.memberCount + this.conversation.previousMembers.length;
  }

  get trimmedDisplayName() {
    return this.conversation.displayName?.trim() || '';
  }

  get groupTitle() {
    if (this.trimmedDisplayName) return this.trimmedDisplayName;

    return this.participantNames || this.intl.t('emc.conversation_list.just_you');
  }

  get showClientDesignator() {
    return this.messaging.currentProfile?.isClinician && this.conversation.containsClient;
  }

  get memberCountLabel() {
    if (this.messaging.currentProfile?.isClinician && this.conversation.previousMembers.length) {
      return this.intl.t('emc.member_count_remaining', {
        current: this.memberCount,
        original: this.originalMemberCount,
      });
    }

    return this.intl.t('emc.member_count', { count: this.memberCount });
  }

  get clientDesignatorLabel() {
    if (!this.isGroup) return this.intl.t('emc.client_conversation');

    return this.intl.t('emc.client_conversation_with_members', {
      memberCount: this.memberCountLabel,
    });
  }

  get visibleMembers() {
    return conversationMembersForDisplay(this.conversation, this.messaging.currentProfile?.user);
  }

  get participantNames() {
    return conversationUserNames(
      this.visibleMembers.map(member => member.name),
      this.conversation.isCouple && !this.isGroup
    );
  }

  get shouldShowClientProfileLink() {
    return this.clientProfileNavigator.canNavigateToClientProfile(
      this.visibleMembers,
      this.messaging.currentProfile.isClinician
    );
  }

  navigateToClientProfileTask = task({ drop: true }, async () => {
    try {
      this.clientProfileNavigator.navigateToClientProfile(this.conversation, this.visibleMembers);
    } catch (error) {
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  handleClientProfileClick = () => {
    document.activeElement.blur();
    this.navigateToClientProfileTask.perform();
  };

  handleGroupHeaderClick = () => {
    this.messagingNavigation.toConversationDetails();
  };

  <template>
    <div class={{styles.component}}>
      <span class="participant-names-container" data-test-participant-names>
        <span class="header-stack">
          {{#if this.isGroup}}
            <button
              type="button"
              class="group-message-header"
              aria-label={{t "emc.conversation_details.view_aria_label"}}
              data-test-group-message-header
              {{on "click" this.handleGroupHeaderClick}}
            >
              <span class="participant-names" data-test-group-message-title>
                {{this.groupTitle}}
              </span>
            </button>
          {{else if this.shouldShowClientProfileLink}}
            <LinkButton
              {{on "click" this.handleClientProfileClick}}
              class="participant-profile-link"
              data-test-client-profile-link
            >
              <span class="participant-names">
                {{this.participantNames}}
              </span>
            </LinkButton>
          {{else}}
            <span class="participant-names">
              {{this.participantNames}}
            </span>
          {{/if}}

          {{#if this.showClientDesignator}}
            <Tag
              @icon="messaging-user"
              @label={{this.clientDesignatorLabel}}
              @small={{true}}
              @type="basic"
              class="client-conversation-designator"
              data-test-client-conversation-designator
            />
          {{else if this.isGroup}}
            <span class="member-count" data-test-group-message-member-count>
              {{this.memberCountLabel}}
            </span>
          {{/if}}
        </span>

        {{#if @showAwayIndicator}}
          <span class="away-indicator" data-test-away-indicator>
            {{t "emc.away_status.indicator"}}
          </span>
        {{/if}}
      </span>
    </div>
  </template>
}
